import { defineStore } from 'pinia';
import { computed, nextTick, ref } from 'vue';

import { HotelDetailsEventSource } from '@apps/main/new-desktop/core/models';
import {
  useHotelDetailsStore,
  useRouterStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';

import { triggerAccommodationHotjarSurvey } from '@libs/hotjar-events';
import type { IngredientId } from '@libs/trip-planner-interfaces';

import type {
  HotelDetailsData,
  SelectStayEventPayload,
  ToggleHeartEventPayload,
} from '../../../modules/hotelDetails';
import type {
  HotelSelectionHotelDetailsPanel,
  HotelSelectionPanel,
} from '../models';
import { HOTEL_SELECTION_NO_PANEL, HotelSelectionPanelType } from '../models';

export const useHotelSelectionPanelsStore = defineStore(
  'HotelSelectionPanelStore',
  () => {
    const routerStore = useRouterStore();
    const statsigEventsStore = useStatsigEventsStore();
    const hotelDetailsStore = useHotelDetailsStore();

    // State
    const hotelSelectionPanel = ref<HotelSelectionPanel>(
      HOTEL_SELECTION_NO_PANEL
    );

    // Actions
    function openHotelDetailsPanel(
      hotelCode: string,
      ingredientId: IngredientId
    ): void {
      const hotelDetailsPanel: HotelSelectionHotelDetailsPanel = {
        type: HotelSelectionPanelType.HotelDetails,
        hotelCode,
        ingredientId,
      };
      hotelSelectionPanel.value = hotelDetailsPanel;
      hotelDetailsStore.openHotelDetails(hotelCode, ingredientId);
    }

    function closeHotelDetailsPanel(): void {
      hotelSelectionPanel.value = HOTEL_SELECTION_NO_PANEL;
    }

    async function selectStay(
      eventPayload: SelectStayEventPayload
    ): Promise<void> {
      statsigEventsStore.logHotelDetailsStaySelected({
        source: HotelDetailsEventSource.HotelSelection,
      });
      triggerAccommodationHotjarSurvey();
      hotelDetailsStore.selectStay(eventPayload);
      closeHotelDetailsPanel();

      // NOTE: We need to wait for the route to be updated with selected hotel before redirecting to overview
      await nextTick();
      await routerStore.navigateToTripOverview();
    }

    async function toggleHeart(
      eventPayload: ToggleHeartEventPayload
    ): Promise<void> {
      statsigEventsStore.logHotelDetailsSelected({
        source: HotelDetailsEventSource.HotelSelection,
      });
      hotelDetailsStore.toggleHeart(
        eventPayload,
        !eventPayload.isPreviouslySelected
      );
      closeHotelDetailsPanel();
      if (!eventPayload.isPreviouslySelected) {
        // NOTE: We need to wait for the route to be updated with selected hotel before redirecting to overview
        await nextTick();
        await routerStore.navigateToTripOverview();
      }
    }

    // Getters
    const hotelSelectionPanelType = computed<HotelSelectionPanelType>(
      () => hotelSelectionPanel.value.type
    );

    const hotelDetailsData = computed<HotelDetailsData | undefined>(() => {
      if (
        hotelSelectionPanel.value.type !== HotelSelectionPanelType.HotelDetails
      ) {
        return;
      }

      return hotelDetailsStore.hotelDetailsData;
    });

    return {
      // Actions
      openHotelDetailsPanel,
      closeHotelDetailsPanel,
      selectStay,
      toggleHeart,

      // Getters
      hotelSelectionPanelType,
      hotelDetailsData,
    };
  }
);
